import {
  init,
  withScope,
  captureException,
  showReportDialog,
  getCurrentScope,
} from '@sentry/browser';
import type { BrowserOptions, User } from '@sentry/browser';

const getSentryConfig = (env: string | undefined): BrowserOptions => {
  const baseConfig: BrowserOptions = {
    dsn: 'https://10fb7af507b44bd69dfd7cc83bdc6370@sentry.io/1326491',
  };
  switch (env) {
    case 'production':
      return {
        ...baseConfig,
        environment: 'production',
      };
    case 'stage':
      return {
        ...baseConfig,
        environment: 'stage',
      };
    case 'development':
      return {
        ...baseConfig,
        environment: 'development',
      };
    case 'emulator':
      // Deliberately no `dsn` - Sentry's SDK treats a missing dsn as "stay
      // disabled" (its own documented way to no-op the whole SDK), rather
      // than pointing the Playwright e2e suite at the real, shared Sentry
      // project. Every method below (report/showReportErrorDialog/setUser)
      // becomes a safe no-op automatically; nothing else in this file
      // needs to change.
      return {};
    default: {
      throw new Error(`[${env}] is an invalid environment.`);
    }
  }
};

const sentryConfig = getSentryConfig(process.env.APP_ENV);
init(sentryConfig);

const handledErrors = new WeakSet<object>();
const markAsHandled = (error: object) => {
  handledErrors.add(error);
};

const Reporter = {
  report: (
    // WeakSet.has/add require an object; callers pass Error (or window
    // error events). Typed as object to match runtime WeakSet constraints
    // without changing the original unconditional .has()/.add() path.
    error: object,
    info: object = {},
    extra: object = {}
  ) => {
    if (handledErrors.has(error)) {
      return;
    }
    withScope((scope) => {
      Object.keys(info).forEach((key) => {
        scope.setExtra(key, (info as Record<string, unknown>)[key]);
      });
      Object.keys(extra).forEach((key) => {
        scope.setExtra(key, (extra as Record<string, unknown>)[key]);
      });
      captureException(error);
    });
    markAsHandled(error);
  },

  // Call sites (CrashReporterErrorBoundary) may pass dialog options; the
  // original implementation ignored them and still only called showReportDialog().
  showReportErrorDialog: (...args: unknown[]) => {
    void args;
    showReportDialog();
  },

  setUser: (obj: User | null) => {
    getCurrentScope().setUser(obj);
  },
};
Object.freeze(Reporter);

window.addEventListener('error', (event) => {
  const { error } = event;
  // Same call as the JS original; window ErrorEvent.error is often undefined
  // for script errors, in which case WeakSet.has throws at runtime.
  Reporter.report(error as object);
});

export default Reporter;
